import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Divider,
  Tooltip,
  Chip,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Snackbar,
  CircularProgress,
  Link,
  Tabs,
  Tab,
  useTheme,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ArrowDropDown, ArrowDropUp, ContentCopy, Settings, Key, VpnKeyOff } from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import { motion } from 'framer-motion';
import Header from '../global/Header';
import { tokens } from '../../../theme';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import MarketPulseTab from './market_pulse_api/MarketPulseTab';
import APIPlanManager from './APIPlanManager';

// RTK Query hooks (existing)
import {
  useGetOrgApiUsageQuery,
  useSetOrgApiDeliveryMutation
} from '../../../features/organizations/organizationsApiSlice';

// --- small helpers ---
const copy = async (text, onDone) => {
  try { await navigator.clipboard.writeText(text); onDone?.('Copied'); }
  catch { onDone?.('Copy failed'); }
};

function useDateRange(daysDefault = 7) {
  const [days, setDays] = useState(daysDefault);
  const end = useMemo(() => new Date(), []);
  const start = useMemo(() => subDays(end, days - 1), [end, days]);
  const startStr = format(start, 'yyyy-MM-dd');
  const endStr = format(end, 'yyyy-MM-dd');
  return { days, setDays, startStr, endStr };
}

const StatBox = ({ label, value, sublabel }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
    <Typography variant="overline" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ mt: .5 }}>{value}</Typography>
    {sublabel ? <Typography variant="caption" color="text.secondary">{sublabel}</Typography> : null}
  </Paper>
);

// --- Settings panel now includes a Delivery-to-S3 section (UI-only for now) ---
const ApiSettingsPanel = ({ organization, onSnack }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const cfg = organization?.api_config || {};

  // Raw bucket name from on-prem config (no s3:// prefix)
  const s3TargetBucket =
    organization?.s3_config?.infra_data?.onprem_config?.onprem_bucket || '';

  const hasS3 = Boolean(s3TargetBucket);

  // Show “APIs at large” prefix like: s3://<bucket>/outgoing/apis/
  const apisAtLargePrefix = hasS3
    ? `s3://${s3TargetBucket}/outgoing/apis/`
    : '';

  const initialDeliverToS3 = Boolean(cfg?.deliver_to_s3);
  const [deliverToS3, setDeliverToS3] = useState(initialDeliverToS3);

  useEffect(() => {
    setDeliverToS3(initialDeliverToS3);
  }, [initialDeliverToS3, organization?.organization_id]);

  // Wire to server
  const [setOrgApiDelivery, { isLoading: s3Busy }] = useSetOrgApiDeliveryMutation();

  const onSaveDelivery = async () => {
    try {
      await setOrgApiDelivery({
        id: organization.organization_id,
        deliver_to_s3: deliverToS3,
      }).unwrap();
      onSnack(`S3 delivery ${deliverToS3 ? 'enabled' : 'disabled'}`);
    } catch (e) {
      onSnack(e?.data?.message || 'Failed to update S3 delivery setting');
    }
  };

  const rows = [
    ['Enabled', cfg.enabled ? 'Yes' : 'No'],
    ['Usage plan ID', cfg.usage_plan_id || '—'],
    ['API key ID', cfg.api_key_id || '—'],
  ];

  // Pretty display strings
  const apisAtLargePath = hasS3 ? `s3://${s3TargetBucket}/outgoing/apis/` : null;

  return (
    <Paper
      elevation={6}
      sx={{
        p: 2,
        borderRadius: 2,
        background: `linear-gradient(135deg, ${colors.primary[500]}, ${colors.primary[800]})`,
        color: colors.grey[100],
      }}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>API Settings</Typography>
      <Divider sx={{ mb: 1, borderColor: 'rgba(255,255,255,0.2)' }} />
      {rows.map(([k, v]) => (
        <Box key={k} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: .75 }}>
          <Typography variant="body2" sx={{ opacity: .85 }}>{k}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{v}</Typography>
            {v && v !== '—' && k !== 'Enabled' ? (
              <Tooltip title="Copy">
                <IconButton size="small" onClick={() => copy(v, onSnack)} sx={{ color: 'inherit', opacity: .85 }}>
                  <ContentCopy fontSize="inherit" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        </Box>
      ))}

      {/* Delivery-to-S3 section */}
      <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.2)' }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Delivery</Typography>
      <Box sx={{ display: 'grid', gap: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={deliverToS3}
              onChange={(e) => setDeliverToS3(e.target.checked)}
              disabled={!hasS3 /* || s3Busy */}
              color="default"
            />
          }
          label={
            <Typography variant="body2" sx={{ opacity: .9 }}>
              Deliver <strong>APIs (all products)</strong> to org S3 bucket
            </Typography>
          }
        />
        <Typography variant="caption" sx={{ opacity: .95 }}>
          {hasS3 ? (
            <>
              APIs at large path:{' '}
              <span style={{ fontFamily: 'monospace' }}>{apisAtLargePath}</span>
            </>
          ) : (
            'No on-prem bucket set on this org'
          )}
        </Typography>
        <Box sx={{ mt: .5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={onSaveDelivery}
            disabled={!hasS3 /* || s3Busy */}
          >
            Save delivery setting
          </Button>
        </Box>
      </Box>

      {/* Org tags */}
      <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.2)' }} />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={organization?.name || '—'} />
        <Chip size="small" label={`Org ID: ${organization?.organization_id || '—'}`} />
      </Box>
    </Paper>
  );
};


function TabPanel({ value, index, children, sx }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2, ...sx }}>{children}</Box>;
}

const ApiDashboard = ({ organization }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const orgId = organization?.organization_id;




  const tabTheme = React.useMemo(() => createTheme({
    components: {
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: colors.greenAccent[500],
            height: 3,
            borderRadius: 3,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            minWidth: 0,
            paddingInline: 12,
            color: colors.grey[300],
            '&.Mui-selected': {
              color: colors.greenAccent[500],
              fontWeight: 600,
            },
            '&:hover': {
              color: colors.greenAccent[300],
              opacity: 0.9,
            },
          },
        },
      },
    },
  }), [colors]);

  // main date range for daily usage
  const { days, setDays, startStr, endStr } = useDateRange(7);

  // UI state
  const [menuEl, setMenuEl] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '' });
  const [settingsOpen, setSettingsOpen] = useState(false); // NEW

  const handleSnack = (msg) => {
    if (msg === '__CLOSE_DIALOG__') {
      setSettingsOpen(false);
      return;
    }
    setSnack(msg);
  };
  const closeSnack = () => setSnack({ open: false, msg: '' });

  const openMenu = (e) => setMenuEl(e.currentTarget);
  const closeMenu = () => setMenuEl(null);

  const [tab, setTab] = useState(0);

  const openSettings = () => {
    setSettingsOpen(true);
    closeMenu();
  };

  // ===== Queries =====
  const { data: usageData, isFetching: usageLoading } =
    useGetOrgApiUsageQuery(orgId ? { id: orgId, start: startStr, end: endStr } : null, { skip: !orgId });

  // ===== Derived rows =====
  const totalCalls = usageData?.total ?? 0;
  const usageRows = (usageData?.daily || []).map((d, i) => ({
    id: i + 1,
    date: d.date,
    calls: d.used
  }));







  // Precompute bucket for children that need to render a path
  const s3TargetBucket =
    organization?.s3_config?.infra_data?.onprem_config?.onprem_bucket || '';

  // ===== Grid columns =====
  const usageColumns = [
    { field: 'date', headerName: 'Day', flex: 1, minWidth: 140 },
    { field: 'calls', headerName: 'Calls', type: 'number', flex: .8, minWidth: 120 },
  ];

  return (
    <Box m="20px">
      {/* Header + Menu */}
      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        <Grid item xs={12} sm="auto">
          <Header title="APIs Dashboard" subtitle={organization?.name || ''} />
        </Grid>
        <Grid item>
          <IconButton onClick={openMenu}>
            {Boolean(menuEl) ? <ArrowDropUp /> : <ArrowDropDown />}
            <Typography variant="p4" color={colors.grey[200]} sx={{ ml: 1 }}>More</Typography>
          </IconButton>
          <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={closeMenu}>
            <MenuItem onClick={openSettings}>
              <Settings fontSize="small" sx={{ mr: 1 }} />
              API Settings
            </MenuItem>
          </Menu>
        </Grid>
      </Grid>

      {/* Top row */}
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={4}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .4 }}>
            <ApiSettingsPanel organization={organization} onSnack={handleSnack} />
          </motion.div>
        </Grid>

        <Grid item xs={12} md={5}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .45 }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={6} md={4}>
                <StatBox label="Total Calls" value={usageLoading ? '—' : totalCalls.toLocaleString()} sublabel={`${startStr} → ${endStr}`} />
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    This dashboard currently shows <strong>Market Pulse</strong> API usage (more products later).
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary">Quick Range</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              {[7, 14, 30].map(n => (
                <Button
                  key={n}
                  size="small"
                  variant={days === n ? 'contained' : 'outlined'}
                  onClick={() => setDays(n)}
                >
                  {n}d
                </Button>
              ))}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Using: <strong>{startStr}</strong> → <strong>{endStr}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              (Change to refresh usage)
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Link href={`/console/organization/${orgId}?tab=APIs`} underline="hover">
              Open API Home
            </Link>
          </Paper>
        </Grid>
      </Grid>

      {/* Bottom: Tabbed section */}
      <Box sx={{ mt: 3 }}>
        <ThemeProvider theme={tabTheme}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="API Usage" />
            <Tab label="Market Pulse" />
          </Tabs>
        </ThemeProvider>

        {/* Tab 0: API Usage */}
        <TabPanel value={tab} index={0}>
          <Typography variant="h6" sx={{ mb: 1 }}>Daily API Usage</Typography>
          <Paper variant="outlined" sx={{ borderRadius: 2, height: 420 }}>
            <DataGrid
              rows={usageRows}
              columns={usageColumns}
              disableRowSelectionOnClick
              loading={usageLoading}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
                sorting: { sortModel: [{ field: 'date', sort: 'asc' }] },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </Paper>
          {!usageLoading && usageRows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No usage data in this range.
            </Typography>
          )}
        </TabPanel>

        {/* Tab 1: Market Pulse */}
        <TabPanel value={tab} index={1}>
          <MarketPulseTab
            organization={organization}
            start={startStr}
            end={endStr}
            s3TargetBucket={s3TargetBucket}
          />
        </TabPanel>
      </Box>

      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogContent sx={{ p: 0 }}>
          <APIPlanManager organization={organization} onSnack={handleSnack} />
        </DialogContent>
      </Dialog>
      {/* snack */}
      <Snackbar open={snack.open} autoHideDuration={2400} onClose={closeSnack} message={snack.msg} />
    </Box>
  );
};

export default ApiDashboard;
